import { Theme as AugmentedTheme } from '@mui/material';

import { makeStyles } from 'tss-react/mui';
import { TFunction, useTranslation } from 'react-i18next';
import React, { useCallback, useEffect, useState } from 'react';
import {
  ApiButton,
  Box,
  ContentTemplate,
  CustomSelect,
  HeaderInput,
  HeaderTemplate,
  SectionHeader,
  Typography,
} from 'web-library';
import { AUTHORITY_NAMES } from 'web-library/models/AuthorityFunctions';
import { configurationTypeData } from '../logic/dropDownOption';
import {
  ConfigurationExamResponse,
  OpenPeriodConfigRequest,
} from '../../../api/candidate/config/model';
import { candidateConfigQuery } from '../../../api/candidate/config';
import ResultReleaseConfiguration from './ResultReleaseConfiguration';
import { useTriggerMutate } from '../../../hooks/query';
import { useGetUserAuthorities } from '../../../api-refactored/user/useGetUserAuthorities';

const useStyles = makeStyles()((theme) =>
({
  formElementContainer: {
    marginBottom: theme.spacing(2),
    width: '30%',
    marginRight: '5%',
    [theme.breakpoints.down('md')]: {
      width: '100%',
    },
  },
  submitButtonContainer: {
    width: '100%',
    display: 'flex',
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(6),
  },
}));

const useFeatureType = (configurationType: string, examValue: string = '') => {
  const [headerText, setHeaderText] = useState('');
  const [examText, setExamText] = useState('');

  const checkHandler = useCallback(() => {
    switch (configurationType) {
      case 'ACCESS_ARRANGEMENT':
        setHeaderText('ACCESS ARRANGEMENT');
        setExamText(examValue);
        break;
      case 'APPEAL':
        setHeaderText('APPEAL');
        setExamText(examValue);
        break;
      case 'AMEND_PROFILE':
        setHeaderText('PROFILE');
        break;
      case 'AMEND_PROFILE_WITH_APPROVAL':
        setHeaderText('PROFILE WITH APPROVAL');
        break;
      case 'APPEAL_FEE_PAYMENT':
        setHeaderText('APPEAL FEE PAYMENT');
        break;
      case 'RESULT_RELEASE':
        setHeaderText('RESULT');
        setExamText(examValue);
        break;
      case 'RESIT':
        setHeaderText('RESIT');
        setExamText(examValue);
        break;
      case 'EXAM_REGISTRATION':
        setHeaderText('EXAM REGISTRATION');
        setExamText(examValue);
        break;
      case 'EXAM_WITHDRAWAL':
        setHeaderText('EXAM WITHDRAWAL');
        setExamText(examValue);
        break;
      case 'SUPPLEMENTARY_REGISTRATION':
        setHeaderText('SUPPLEMENTARY REGISTRATION');
        setExamText(examValue);
        break;
      case 'SPECIAL_CONSIDERATION':
        setHeaderText('SPECIAL CONSIDERATION');
        setExamText(examValue);
        break;
      case 'SUBJECT_AMENDMENT':
        setHeaderText('SUBJECT AMENDMENT');
        setExamText(examValue);
        break;
      default:
        setHeaderText('');
        break;
    }
  }, [configurationType, examValue]);

  return {
    headerText,
    examText,
    checkHandler,
  };
};

const getAccessArrangementSectionHeader = (examText: string, t: TFunction<'translation'>) => {
  if (examText === 'GCEA') {
    return (
      <SectionHeader
        title={t('admin->>AdminFeatureConfiguration.AccessArrangementConfigurationForExamLevel', {
          examLevel: 'GCE A-Level',
        })}
      />
    );
  }
  if (examText === 'GCEO') {
    return (
      <SectionHeader
        title={t('admin->>AdminFeatureConfiguration.AccessArrangementConfigurationForExamLevel', {
          examLevel: 'GCE O-Level',
        })}
      />
    );
  }
  if (examText === 'GCEN') {
    return (
      <SectionHeader
        title={t('admin->>AdminFeatureConfiguration.AccessArrangementConfigurationForExamLevel', {
          examLevel: 'GCE N-Level',
        })}
      />
    );
  }
  return (
    <SectionHeader
      title={t('admin->>AdminFeatureConfiguration.AccessArrangementConfigurationForExamLevel', {
        examLevel: 'PSLE',
      })}
    />
  );
};

const getAppealSectionHeader = (examText: string, t: TFunction<'translation'>) => {
  if (examText === 'GCEA') {
    return (
      <SectionHeader
        title={t('admin->>AdminFeatureConfiguration.AppealConfigurationForExamLevel', {
          examLevel: 'GCE A-Level',
        })}
      />
    );
  }
  if (examText === 'GCEO') {
    return (
      <SectionHeader
        title={t('admin->>AdminFeatureConfiguration.AppealConfigurationForExamLevel', {
          examLevel: 'GCE O-Level',
        })}
      />
    );
  }
  if (examText === 'GCEN') {
    return (
      <SectionHeader
        title={t('admin->>AdminFeatureConfiguration.AppealConfigurationForExamLevel', {
          examLevel: 'GCE N-Level',
        })}
      />
    );
  }
  return (
    <SectionHeader
      title={t('admin->>AdminFeatureConfiguration.AppealConfigurationForExamLevel', {
        examLevel: 'PSLE',
      })}
    />
  );
};

const getResultSectionHeader = (examText: string, t: any) => {
  if (examText === 'GCEO') {
    return (
      <SectionHeader
        title={t('admin->>AdminFeatureConfiguration.Results Release Configuration for GCE O-Level')}
      />
    );
  }
  if (examText === 'GCEA') {
    return (
      <SectionHeader
        title={t('admin->>AdminFeatureConfiguration.Results Release Configuration for GCE A-Level')}
      />
    );
  }
  if (examText === 'GCEN') {
    return (
      <SectionHeader
        title={t('admin->>AdminFeatureConfiguration.Results Release Configuration for GCE N-Level')}
      />
    );
  }
  if (examText === 'PSLE') {
    return (
      <SectionHeader
        title={t('admin->>AdminFeatureConfiguration.Results Release Configuration for PSLE')}
      />
    );
  }
  if (examText === 'IPSLE') {
    return (
      <SectionHeader
        title={t('admin->>AdminFeatureConfiguration.Results Release Configuration for iPSLE')}
      />
    );
  }
  return null;
};

const getExamRegistrationSectionHeader = (examText: string, t: any) => {
  if (examText === 'GCEO') {
    return (
      <SectionHeader
        title={t(
          'admin->>AdminFeatureConfiguration.Exam Registration Configuration for GCE O-Level'
        )}
      />
    );
  }
  if (examText === 'GCEA') {
    return (
      <SectionHeader
        title={t(
          'admin->>AdminFeatureConfiguration.Exam Registration Configuration for GCE A-Level'
        )}
      />
    );
  }
  if (examText === 'GCEN') {
    return (
      <SectionHeader
        title={t(
          'admin->>AdminFeatureConfiguration.Exam Registration Configuration for GCE N-Level'
        )}
      />
    );
  }
  if (examText === 'SAEIS') {
    return (
      <SectionHeader
        title={t('admin->>AdminFeatureConfiguration.Exam Registration Configuration for S-AEIS')}
      />
    );
  }
  if (examText === 'IPSLE') {
    return (
      <SectionHeader
        title={t(
          'admin->>AdminFeatureConfiguration.Exam Registration Configuration for {{examText}}',
          { examText: 'iPSLE' }
        )}
      />
    );
  }
  return (
    <SectionHeader
      title={t(
        'admin->>AdminFeatureConfiguration.Exam Registration Configuration for {{examText}}',
        { examText }
      )}
    />
  );
};

const getExamWithdrawalSectionHeader = (examText: string, t: any) => {
  if (examText === 'GCEA') {
    return (
      <SectionHeader
        title={t('admin->>AdminFeatureConfiguration.ExamWithdrawalConfigurationForExamLevel', {
          examLevel: 'GCE A-Level',
        })}
      />
    );
  }
  if (examText === 'GCEO') {
    return (
      <SectionHeader
        title={t('admin->>AdminFeatureConfiguration.ExamWithdrawalConfigurationForExamLevel', {
          examLevel: 'GCE O-Level',
        })}
      />
    );
  }
  if (examText === 'GCEN') {
    return (
      <SectionHeader
        title={t('admin->>AdminFeatureConfiguration.ExamWithdrawalConfigurationForExamLevel', {
          examLevel: 'GCE N-Level',
        })}
      />
    );
  }
  if (examText === 'SAEIS') {
    return (
      <SectionHeader
        title={t('admin->>AdminFeatureConfiguration.ExamWithdrawalConfigurationForExamLevel', {
          examLevel: 'S-AEIS',
        })}
      />
    );
  }
  if (examText === 'IPSLE') {
    return (
      <SectionHeader
        title={t('admin->>AdminFeatureConfiguration.ExamWithdrawalConfigurationForExamLevel', {
          examLevel: 'iPSLE',
        })}
      />
    );
  }
  return (
    <SectionHeader
      title={t('admin->>AdminFeatureConfiguration.ExamWithdrawalConfigurationForExamLevel', {
        examLevel: examText,
      })}
    />
  );
};

const getSpecialConsiderationSectionHeader = (examText: string, t: TFunction<'translation'>) => {
  if (examText === 'GCEA') {
    return (
      <SectionHeader
        title={t(
          'admin->>AdminFeatureConfiguration.SpecialConsiderationConfigurationForExamLevel',
          {
            examLevel: 'GCE A-Level',
          }
        )}
      />
    );
  }
  if (examText === 'GCEO') {
    return (
      <SectionHeader
        title={t(
          'admin->>AdminFeatureConfiguration.SpecialConsiderationConfigurationForExamLevel',
          {
            examLevel: 'GCE O-Level',
          }
        )}
      />
    );
  }
  if (examText === 'GCEN') {
    return (
      <SectionHeader
        title={t(
          'admin->>AdminFeatureConfiguration.SpecialConsiderationConfigurationForExamLevel',
          {
            examLevel: 'GCE N-Level',
          }
        )}
      />
    );
  }
  return (
    <SectionHeader
      title={t('admin->>AdminFeatureConfiguration.SpecialConsiderationConfigurationForExamLevel', {
        examLevel: 'PSLE',
      })}
    />
  );
};

const getSupplementaryRegistrationSectionHeader = (examText: string, t: any) => {
  if (examText === 'GCEO') {
    return (
      <SectionHeader
        title={t(
          'admin->>AdminFeatureConfiguration.Supplementary Registration Configuration for GCE O-Level'
        )}
      />
    );
  }
  if (examText === 'GCEA') {
    return (
      <SectionHeader
        title={t(
          'admin->>AdminFeatureConfiguration.Supplementary Registration Configuration for GCE A-Level'
        )}
      />
    );
  }
  if (examText === 'GCEN') {
    return (
      <SectionHeader
        title={t(
          'admin->>AdminFeatureConfiguration.Supplementary Registration Configuration for GCE N-Level'
        )}
      />
    );
  }
  if (examText === 'IPSLE') {
    return (
      <SectionHeader
        title={t(
          'admin->>AdminFeatureConfiguration.Supplementary Registration Configuration for iPSLE'
        )}
      />
    );
  }
  if (examText === 'PSLE') {
    return (
      <SectionHeader
        title={t(
          'admin->>AdminFeatureConfiguration.Supplementary Registration Configuration for PSLE'
        )}
      />
    );
  }
  if (examText === 'SAEIS') {
    return (
      <SectionHeader
        title={t(
          'admin->>AdminFeatureConfiguration.Supplementary Registration Configuration for S-AEIS'
        )}
      />
    );
  }
  return (
    <SectionHeader
      title={t(
        'admin->>AdminFeatureConfiguration.Supplementary Registration Configuration for {{examText}}',
        { examText }
      )}
    />
  );
};

const getSubjectAmendmentSectionHeader = (examText: string, t: any) => {
  if (examText === 'GCEO') {
    return (
      <SectionHeader
        title={t(
          'admin->>AdminFeatureConfiguration.Subject Amendment Configuration for GCE O-Level'
        )}
      />
    );
  }
  if (examText === 'GCEA') {
    return (
      <SectionHeader
        title={t(
          'admin->>AdminFeatureConfiguration.Subject Amendment Configuration for GCE A-Level'
        )}
      />
    );
  }
  if (examText === 'GCEN') {
    return (
      <SectionHeader
        title={t(
          'admin->>AdminFeatureConfiguration.Subject Amendment Configuration for GCE N-Level'
        )}
      />
    );
  }
  if (examText === 'IPSLE') {
    return (
      <SectionHeader
        title={t('admin->>AdminFeatureConfiguration.Subject Amendment Configuration for iPSLE')}
      />
    );
  }
  if (examText === 'PSLE') {
    return (
      <SectionHeader
        title={t('admin->>AdminFeatureConfiguration.Subject Amendment Configuration for PSLE')}
      />
    );
  }
  return null;
};

const useFeatureHeader = (headerText: string, examText: string) => {
  const { t } = useTranslation();
  let content;

  switch (headerText) {
    case 'ACCESS ARRANGEMENT':
      content = getAccessArrangementSectionHeader(examText, t);
      break;

    case 'APPEAL':
      content = getAppealSectionHeader(examText, t);
      break;

    case 'PROFILE':
      content = (
        <SectionHeader
          title={t('admin->>AdminFeatureConfiguration.Candidate Profile Amendment Configuration')}
        />
      );
      break;

    case 'PROFILE WITH APPROVAL':
      content = (
        <SectionHeader
          title={t(
            'admin->>AdminFeatureConfiguration.CandidateProfileAmendmentWithApprovalConfiguration'
          )}
        />
      );
      break;

    case 'SPECIAL CONSIDERATION':
      content = getSpecialConsiderationSectionHeader(examText, t);
      break;

    case 'RESULT':
      content = getResultSectionHeader(examText, t);
      break;

    case 'RESIT':
      if (examText === 'GCEO') {
        content = (
          <SectionHeader
            title={t('admin->>AdminFeatureConfiguration.Resit Configuration for GCE O-Level')}
          />
        );
      } else if (examText === 'GCEA') {
        content = (
          <SectionHeader
            title={t('admin->>AdminFeatureConfiguration.Resit Configuration for GCE A-Level')}
          />
        );
      }
      break;

    case 'EXAM REGISTRATION':
      content = getExamRegistrationSectionHeader(examText, t);
      break;

    case 'EXAM WITHDRAWAL':
      content = getExamWithdrawalSectionHeader(examText, t);
      break;

    case 'SUPPLEMENTARY REGISTRATION':
      content = getSupplementaryRegistrationSectionHeader(examText, t);
      break;

    case 'SUBJECT AMENDMENT':
      content = getSubjectAmendmentSectionHeader(examText, t);
      break;

    default:
      break;
  }

  return content;
};

const AdminFeatureConfiguration = () => {
  const { classes } = useStyles();
  const { t } = useTranslation();

  const [configurationType, setConfigurationType] = useState<string>('');
  const [exam, setExam] = useState<ConfigurationExamResponse[]>([]);
  const [examValue, setExamValue] = useState<string | undefined>('');
  const [disableExamLevelSelection, setDisableExamLevelSelection] = useState<boolean>(false);
  const [searchTouched, setSearchTouched] = useState(false);
  const [initData, setInitData] = useState<OpenPeriodConfigRequest>({
    configurationType: '',
    examLevelCode: '',
  });

  const { data: userAuthoritiesData = [] } = useGetUserAuthorities();
  const isEnableEdit = userAuthoritiesData.includes(
    AUTHORITY_NAMES.CP_ADMIN_FEATURES_CONFIGURATION_EDIT
  );

  const { headerText, examText, checkHandler } = useFeatureType(configurationType, examValue);

  const { data: dataConfigurationType } = candidateConfigQuery.useFeatureConfigurationgQuery();

  const { mutate } = candidateConfigQuery.useRetrieveConfigurationExamList(
    (res) => {
      setExam(res.data);
      if(res.data.length === 0){
        setDisableExamLevelSelection(true);
        setExamValue(undefined);
      } else {
        setDisableExamLevelSelection(false);
        setExamValue(res.data[0].code);
      }
    },
    () => null
  );

  const onConfigationTypeHandler = (event: any) => {
    setConfigurationType(event.target.value);
  };

  const onExamChangedHandler = (event: any) => {
    setExamValue(event.target.value);
  };

  const callMutation = useCallback(() => {
    if (configurationType !== '') {
      mutate({
        q: configurationType,
      });
    }
  }, [mutate, configurationType]);
  const setTriggerMutate = useTriggerMutate(callMutation);

  useEffect(() => {
    setTriggerMutate((prev) => prev + 1);
  }, [configurationType, setTriggerMutate]);

  useEffect(() => {
    if (dataConfigurationType) {
      setConfigurationType(dataConfigurationType[0].code);
    }
  }, [dataConfigurationType]);

  // Check if Search contain any error
  const onSearchClickedHandler = () => {
    if (examValue !== '' && configurationType !== '') {
      setSearchTouched(true);
      setInitData({
        configurationType,
        examLevelCode: examValue,
      });
      checkHandler();
      return;
    }
    setSearchTouched(false);
  };

  const content = useFeatureHeader(headerText, examText);

  return (
    <Box>
      {dataConfigurationType && exam && (
        <HeaderTemplate>
          <HeaderInput
            label={t('admin->>AdminFeatureConfiguration.Feature Type')}
            input={
              <CustomSelect
                id="inputSelect-featureType"
                isHeader
                onChange={onConfigationTypeHandler}
                menuItems={configurationTypeData(dataConfigurationType)}
                value={configurationType}
                variant="filled"
              />
            }
          />

          <HeaderInput
            label={t('admin->>AdminFeatureConfiguration.Exam Level')}
            input={
              <CustomSelect
                id="inputSelect-examLevel"
                isHeader
                onChange={onExamChangedHandler}
                menuItems={configurationTypeData(exam)}
                value={examValue || "-"}
                variant="filled"
                disabled={disableExamLevelSelection}
                placeholder={examValue ? "" : "-"}
                emptyItemValue="-"
                displayEmpty
              />
            }
          />
          <Box className={classes.submitButtonContainer}>
            <ApiButton
              id="button-featureConfiguration-applySelection"
              status="idle"
              type="button"
              onClick={onSearchClickedHandler}
            >
              {t('admin->>AdminFeatureConfiguration.Apply Selection')}
            </ApiButton>
          </Box>
        </HeaderTemplate>
      )}
      {searchTouched && (
        <ContentTemplate>
          {content}

          <Typography>
            {t('admin->>AdminFeatureConfiguration.Select the window period.')}
          </Typography>
          <ResultReleaseConfiguration initData={initData} isEnableEdit={isEnableEdit} />
        </ContentTemplate>
      )}
    </Box>
  );
};

export default AdminFeatureConfiguration;
